import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
}

interface SelectProps {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  icon?: string;
  ariaLabel?: string;
  className?: string;
  size?: "md" | "lg";
  invalid?: boolean;
}

interface PanelPosition {
  top: number;
  left: number;
  width: number;
  openUp: boolean;
}

export default function Select({
  value,
  options,
  onChange,
  placeholder = "",
  icon,
  ariaLabel,
  className = "",
  size = "md",
  invalid = false,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<PanelPosition>({
    top: 0,
    left: 0,
    width: 0,
    openUp: false,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selected = options.find((option) => option.value === value);
  const hasValue = Boolean(selected);

  const updatePosition = useCallback(() => {
    const button = buttonRef.current;
    if (!button) {
      return;
    }
    const rect = button.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 300 && rect.top > spaceBelow;
    setPosition({
      top: openUp ? rect.top - 8 : rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }
    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(target) &&
        panelRef.current &&
        !panelRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`w-full ${size === "lg" ? "h-12" : "h-11"} flex items-center gap-2 rounded-btn border bg-white ps-3 pe-3 text-sm outline-none transition-colors duration-200 cursor-pointer ${
          open
            ? "border-primary-300 ring-2 ring-primary-300"
            : invalid
              ? "border-red-300 hover:border-red-400"
              : "border-background-200 hover:border-primary-300"
        }`}
      >
        {icon && (
          <span className="w-5 h-5 shrink-0 flex items-center justify-center text-foreground-400">
            <i className={icon}></i>
          </span>
        )}
        <span
          className={`flex-1 truncate text-start ${hasValue ? "text-foreground-800" : "text-foreground-400"}`}
        >
          {selected ? selected.label : placeholder}
        </span>
        <span
          className={`w-5 h-5 shrink-0 flex items-center justify-center text-foreground-400 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        >
          <i className="ri-arrow-down-s-line"></i>
        </span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            dir="rtl"
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: position.width,
              transform: position.openUp ? "translateY(-100%)" : undefined,
            }}
            className="yc-pop z-[999] rounded-card border border-background-200 bg-white shadow-pop overflow-hidden"
          >
            <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
              {options.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        onChange(option.value);
                        setOpen(false);
                      }}
                      className={`w-full flex ${
                        option.description ? "items-start" : "items-center"
                      } justify-between gap-2 px-4 py-2.5 text-sm text-start transition-colors duration-200 cursor-pointer ${
                        isSelected
                          ? "bg-primary-50 font-semibold text-primary-700"
                          : "text-foreground-700 hover:bg-background-50"
                      }`}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{option.label}</span>
                        {option.description && (
                          <span
                            className={`mt-0.5 block text-xs font-normal leading-5 ${
                              isSelected
                                ? "text-primary-600"
                                : "text-foreground-500"
                            }`}
                          >
                            {option.description}
                          </span>
                        )}
                      </span>
                      {isSelected && (
                        <span
                          className={`w-4 h-4 shrink-0 flex items-center justify-center text-primary-600 ${
                            option.description ? "mt-0.5" : ""
                          }`}
                        >
                          <i className="ri-check-line"></i>
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>,
          document.body,
        )}
    </div>
  );
}